import { useEffect, useRef, useState } from 'react';
import {
  MEASURES_SHIFT,
  MEASURES_VISIBLE,
  pageForWindowStart,
  slidingWindowStart,
  totalSlidingPages,
  visibleSlidingMeasures,
} from '../utils/measurePages';

export type SlideDirection = 'next' | 'prev' | 'none';

export interface MeasurePaginationOptions {
  visible?: number;
  shift?: number;
}

export function useMeasurePagination(
  measureCount: number,
  activeMeasureIndex: number | null,
  enabled: boolean,
  options: MeasurePaginationOptions = {},
) {
  const visible = options.visible ?? MEASURES_VISIBLE;
  const shift = options.shift ?? MEASURES_SHIFT;

  const [windowStart, setWindowStart] = useState(0);
  const [slide, setSlide] = useState<SlideDirection>('none');

  const pages = totalSlidingPages(measureCount, visible, shift);
  const page = pageForWindowStart(windowStart, shift);
  const visibleIndices = visibleSlidingMeasures(
    enabled ? activeMeasureIndex : null,
    measureCount,
    visible,
    shift,
  );

  useEffect(() => {
    if (!enabled) {
      setWindowStart(0);
      setSlide('none');
      return;
    }
    if (activeMeasureIndex === null) return;

    const target = slidingWindowStart(activeMeasureIndex, measureCount, visible, shift);
    if (target === windowStart) return;

    setSlide(target > windowStart ? 'next' : 'prev');
    setWindowStart(target);
  }, [activeMeasureIndex, enabled, measureCount, shift, visible, windowStart]);

  const wasEnabled = useRef(false);
  useEffect(() => {
    if (enabled && !wasEnabled.current) {
      setWindowStart(0);
      setSlide('none');
    }
    wasEnabled.current = enabled;
    if (!enabled) {
      setSlide('none');
    }
  }, [enabled]);

  useEffect(() => {
    if (measureCount <= visible) return;
    const maxStart = measureCount - visible;
    if (windowStart > maxStart) {
      setWindowStart(Math.max(0, maxStart));
    }
  }, [measureCount, visible, windowStart]);

  return {
    page,
    pages,
    slide,
    windowStart,
    visibleIndices,
  };
}
